define([
  'underscore',
  'es6!launcher/util/listbuilder/wysiwyg/constants',
  'es6!launcher/util/listbuilder/wysiwyg/util',
  'es6!launcher/util/listbuilder/constants',
], (_, WysiwygConstants, WysiwygUtil, Constants) => {
  // Default id for animations, templates and template groups.
  const DEFAULT_ID = 'd1b28f5d-d834-4eab-bed8-505338512f5d';
  const { ResponsiveFormats, FormTypes } = Constants;

  function _clearPopupJsonErrors(popupJson) {
    if (!popupJson) return;
    popupJson.errors = undefined;

    const components = getNestedComponents(popupJson);
    for (let i = 0; i < components.length; i++) {
      components[i].errors = undefined;
    }
  }

  function getNestedComponents(popupJson, responsiveFormat) {
    if (!popupJson) return [];

    let components = [];
    _.each(popupJson.components || popupJson.childComponents, (component) => {
      if (component.responsiveHiddenOn && component.responsiveHiddenOn[responsiveFormat]) return;

      if (component.childComponents && component.childComponents.length) {
        components = components.concat(getNestedComponents(component, responsiveFormat));
      } else {
        components.push(component);
      }
    });

    return components;
  }

  function _getDefaultTemplatePopupJsonForPopupType(goal, popupType, fullState) {
    const templates = fullState.templates.get('byId').toJS();
    const defaultTemplate = _.findWhere(templates, { group_id: DEFAULT_ID, type: goal });
    // Can't do anything without the default template.
    // Return false since true would try to modify the popup more than likely.
    if (!defaultTemplate) return null;

    const templateParsedJson = JSON.parse(defaultTemplate.json || '{}');
    return templateParsedJson[popupType];
  }

  // Checks for duplicate field names.
  function _validateComponentFieldNames(components) {
    if (!components || !components.length) return;
    // Hold indexes that have the given name.
    const componentNames = {};

    for (let i = 0; i < components.length; i++) {
      const component = components[i] || {};
      // Make sure there is an error obj.
      component.errors = component.errors || {};
      if (WysiwygConstants.FIELD_COMPONENTS.indexOf(component.type) < 0) continue;
      const name = component.name;

      if (!name || !name.length) {
        component.errors.name = 'Field name can\'t be blank';
        component.errors.location = WysiwygConstants.ACCORDION_TABS.DESIGN;
        continue;
      }

      componentNames[name] = componentNames[name] || [];
      componentNames[name].push(i);

      // If there are more than one components with this name, then we
      // put name errors on both.
      if (componentNames[name].length > 1) {
        _.each(componentNames[name], index => {
          const componentWithSameName = components[index];
          // Make sure there is an error obj.
          componentWithSameName.errors = componentWithSameName.errors || {};

          // If a name error already exists, we don't want to change it.
          if (componentWithSameName.errors.name) return;

          componentWithSameName.errors.name = 'Another field has this name.';
          componentWithSameName.errors.location = WysiwygConstants.ACCORDION_TABS.DESIGN;
        });
      }
    }
  }

  // We need to make sure the redirect to field is valid.
  function _validateComponentClickActionRedirects(components) {
    const httpRegex = /^https?:\/\/[0-9a-zA-Z]+/;
    const telRegex = /^tel:[\+]?[(]?[0-9]/;
    const mailtoRegex = /^mailto:[\+]?[(]?[0-9a-zA-Z]/;

    _.each(components, component => {
      const { action, to, redirectMethod, redirectVariableName } = component;

      if (action === 'redirect') {
        if (!to || !to.length) {
          component.errors = component.error || {};
          component.errors.to = 'The URL can\'t be blank.';
        } else if (!httpRegex.test(to) && !telRegex.test(to) && !mailtoRegex.test(to)) {
          component.errors = component.error || {};
          component.errors.to = 'The URL needs to start with http://, https://, tel:, or mailto:.';
        }

        if (['', 'get', 'post'].indexOf(redirectMethod) < 0) {
          component.errors.redirectMethod = 'Invalid redirect method.';
        }

        if (!redirectVariableName || !redirectVariableName.length) {
          component.errors.redirectVariableName = 'The variable name can\'t be blank';
        }
      }
    });
  }

  // We need to make sure the content upgrade media id is valid.
  function _validateComponentClickActionContentUpgrades(components) {
    _.each(components, component => {
      const { action, to, contentUpgradeMediaId } = component;

      if (action === 'content') {
        if (!contentUpgradeMediaId || !contentUpgradeMediaId.length) {
          component.errors.contentUpgradeMediaId = 'Content upgrade must be selected.';
        }
      }
    });
  }

  // Check certain popups for 'optout' or 'close' actions
  function _validateComponentCloseOptout(components, popup, popupType) {
    if ([
      FormTypes.POPUP,
      FormTypes.SCROLL_BOX,
      FormTypes.CLICK_TRIGGER_POPUP,
    ].indexOf(popup.form_type) > -1) {
      const isSocialProof = popup.goal === Constants.FormGoals.SOCIAL_PROOF;
      const capitalizeName = popupType.charAt(0).toUpperCase() + popupType.slice(1);
      const validActions = _.some(components, component => (
        (component.action === 'optout' || component.action === 'close')
      ));

      if (!validActions && components.length && !isSocialProof) {
        popup.errors.name = `A 'Close' or 'Optout' button or image required in the ${capitalizeName} tab`;
        popup.errors.location = WysiwygConstants.ACCORDION_TABS.DESIGN;
      }
    }
  }

  // Validation functions for popups.
  // These functions go through the popup and validate database attrs
  // and popup json.  When it finds an error, it adds an error object
  // to that level of the popup with the key being the attr name and the
  // value being the data needed to render the error.
  function validatePopup(popup, fullState) {
    const settings = fullState.listbuilderSettings.toJS();
    const { hasLoaded, isScrollBoxUnlocked, isSmartBarUnlocked, isWelcomeMatUnlocked } = settings;
    const popups = fullState.popups.toJS();
    const hasNameChanged = fullState.popups.get('hasNameChanged');
    const parsedJson = JSON.parse(popup.json || '{}');
    const { goal } = popup;
    const httpRegex = /^https?:\/\/[0-9a-zA-Z]+/;

    // Remove old errors on popup database object.
    popup.errors = {};

    // Validate subscribe redirect url
    if (popup.subscribe_redirect_url && !httpRegex.test(popup.subscribe_redirect_url)) {
      popup.errors.name = 'The subscribe redirect URL needs to start with http:// or https://.';
      popup.errors.location = WysiwygConstants.ACCORDION_TABS.SUCCESS;
    }

    // Ensure advanced_form exists for custom form goals
    if (goal === 'custom' && !popup.advanced_form) {
      popup.errors.name = 'Custom form required for Custom Code goal';
      popup.errors.location = WysiwygConstants.ACCORDION_TABS.DESIGN;
    }

    // Ensure people aren't running both versions of Scroll Box
    if (popup.form_type === FormTypes.SCROLL_BOX && !isScrollBoxUnlocked) {
      popup.errors.name = 'Import your old scroll boxes to use scroll boxes in List Builder 3';
      popup.errors.location = WysiwygConstants.ACCORDION_TABS.FORM_TYPE;
    }

    // Ensure people aren't running both versions of Smart Bar
    if (popup.form_type === FormTypes.SMART_BAR && !isSmartBarUnlocked) {
      popup.errors.name = 'Import your old smart bars to use smart bars in List Builder 3';
      popup.errors.location = WysiwygConstants.ACCORDION_TABS.FORM_TYPE;
    }

    // Ensure people aren't running both versions of Welcome Mat
    if (popup.form_type === 'welcome-mat' && !isWelcomeMatUnlocked) {
      popup.errors.name = 'Import your old welcome mats to use welcome mats in List Builder 3';
      popup.errors.location = WysiwygConstants.ACCORDION_TABS.FORM_TYPE;
    }

    if (
      popup.form_type === 'click-trigger-popup'
      && popup.click_triggers
      && popup.click_triggers.length < 1
    ) {
      popup.errors.name = 'Please add a Click Trigger before Making Live';
      popup.errors.location = WysiwygConstants.ACCORDION_TABS.VISIBILITY;
    }

    // Go through the popup jsons in parseJson and add errors.
    _.each(_.keys(parsedJson), popupType => {
      let popupJson = parsedJson[popupType] || {};

      _clearPopupJsonErrors(popupJson);

      _.each(popup.form_type === FormTypes.WELCOME_MAT ? ResponsiveFormats : ['medium'], (format) => {
        const nestedComponents = getNestedComponents(popupJson, format);
        _validateComponentCloseOptout(nestedComponents, popup, popupType);
        _validateComponentFieldNames(nestedComponents);
        _validateComponentClickActionRedirects(nestedComponents);
        _validateComponentClickActionContentUpgrades(nestedComponents);
      });

      parsedJson[popupType] = popupJson;
    });

    // After updating components with errors, we need to set it to the json.
    popup.json = JSON.stringify(parsedJson);

    // Auto-populate popup name when none exists
    if (!hasNameChanged) {
      popup.name = WysiwygUtil.generatePopupName(parsedJson, new Date(popup.created_at));
    }

    // If there aren't errors on the database popup obj, we remove the errors object.
    if (!_.keys(popup.errors).length) popup.errors = undefined;
  }

  return {
    validate: validatePopup,
  };
});
